import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle, Check, MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PhoneInput, { Honeypot, isValidPhone } from "@/components/PhoneInput";
import { useLang } from "@/i18n";
import { api, track, waLink, formatApiError, loadRazorpay, getSrc } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { SERVICES } from "@/data/content";
import ConsentTick from "@/components/ConsentTick";
import LocationPicker from "@/components/LocationPicker";
import ThankYouModal from "@/components/ThankYouModal";
import QrPay from "@/components/QrPay";
import { toast } from "sonner";
import { Step, ServiceChips, ServiceScope, DateChips, HoursStepper, TimePicker, WINDOWS, rangeLabel, startHour } from "@/components/BookingPickers";

const SVC_ORDER = [...SERVICES.filter((s) => s.id !== "shopping"), ...SERVICES.filter((s) => s.id === "shopping")];

const inputCls = "w-full h-12 px-4 rounded-xl border border-[#E5E0D8] bg-white text-[#121316] outline-none focus:border-[#E05624] focus:ring-2 focus:ring-[#E05624]/20 transition";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-bold text-[#121316] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function LegalLinks({ t }) {
  return (
    <p className="text-xs text-center text-[#5A5D65]">
      <Link data-testid="form-terms-link" to="/terms" className="underline">{t("link_terms")}</Link> · <Link data-testid="form-privacy-link" to="/privacy" className="underline">{t("link_privacy")}</Link>
    </p>
  );
}

export default function Booking() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const settings = useSettings();
  const [mode, setMode] = useState(params.get("waitlist") === "1" ? "waitlist" : "book");
  const refCode = params.get("ref") || localStorage.getItem("chela_ref") || "";

  useEffect(() => {
    track("page_view", { page: "booking" });
    if (params.get("ref")) localStorage.setItem("chela_ref", params.get("ref"));
  }, [params]);

  const cities = settings.cities;
  const liveCities = cities.filter((c) => c.status === "live");

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <Header />
      <div className="chela-container py-10 sm:py-16 max-w-3xl">
        <button data-testid="booking-back" onClick={() => navigate("/")} className="inline-flex items-center gap-2 text-sm font-semibold text-[#5A5D65] hover:text-[#E05624]">
          <ArrowLeft size={16} /> {t("back_home")}
        </button>
        <div className="mt-6 flex gap-2 bg-[#F3EFEA] p-1.5 rounded-full w-fit">
          <button data-testid="mode-book" onClick={() => setMode("book")} className={`px-5 h-10 rounded-full text-sm font-bold transition-colors ${mode === "book" ? "bg-[#121316] text-white" : "text-[#5A5D65]"}`}>{t("book")}</button>
          <button data-testid="mode-waitlist" onClick={() => setMode("waitlist")} className={`px-5 h-10 rounded-full text-sm font-bold transition-colors ${mode === "waitlist" ? "bg-[#121316] text-white" : "text-[#5A5D65]"}`}>{t("join_waitlist")}</button>
        </div>
        {mode === "book"
          ? <BookForm t={t} lang={lang} cities={liveCities} price={settings.base_hourly_price} currency={settings.currency} navigate={navigate} refCode={refCode} onWaitlist={() => setMode("waitlist")} />
          : <WaitlistForm t={t} cities={cities} />}
      </div>
      <Footer />
    </div>
  );
}

function BookForm({ t, lang, cities, price, currency, navigate, refCode, onWaitlist }) {
  const [form, setForm] = useState({ city: cities[0]?.name || "Bhopal", service: SVC_ORDER[0].id, description: "", date: "", time: "", duration: "1 Hour", location: "", name: "", phone: "", email: "", referral_code: refCode || "", website: "", consent: false });
  const [more, setMore] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [paid, setPaid] = useState(false);
  const [paying, setPaying] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");
  const [coords, setCoords] = useState(null);
  const [showTy, setShowTy] = useState(false);
  const [payPending, setPayPending] = useState(false);

  const reset = () => { setResult(null); setPayPending(false); };
  const set = (k) => (e) => { reset(); setForm((f) => ({ ...f, [k]: e.target.value })); };
  const put = (k) => (v) => { if (k !== "consent") { reset(); } setForm((f) => ({ ...f, [k]: v })); };
  const setDate = (v) => { reset(); setForm((f) => ({ ...f, date: v, time: "" })); };
  const hours = parseInt(form.duration, 10) || 1;
  const setHours = (n) => {
    reset();
    setForm((f) => {
      const h = WINDOWS.some((w) => w[0] === f.time) ? null : startHour(f.time);
      const time = h == null ? f.time : h + n <= 21 ? rangeLabel(h, n) : "";
      return { ...f, duration: `${n} Hour${n > 1 ? "s" : ""}`, time };
    });
  };
  const estAmount = result?.amount ?? price * (parseInt(form.duration, 10) || 1);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t("required")); return; }
    if (!isValidPhone(form.phone)) { setPhoneErr(t("phone_err")); return; }
    setPhoneErr("");
    if (!form.date || !form.time) { toast.error(t("slot_hint")); return; }
    if (!coords && !form.location.trim()) { toast.error(t("loc_need")); return; }
    if (!form.consent) { toast.error(t("consent_err")); return; }
    if (result) { payNow(result); return; }
    setSubmitting(true);
    const serviceObj = SERVICES.find((s) => s.id === form.service);
    const serviceTitle = serviceObj ? serviceObj.en.title : form.service;
    try {
      const { data } = await api.post("/bookings", { ...form, ...(coords || {}), service: serviceTitle, src: getSrc() });
      track("booking_submit", { city: form.city, service: form.service });
      const map = coords ? `\n📍 Map: https://maps.google.com/?q=${coords.lat},${coords.lng}` : "";
      const msg = `Hi CHELA! I just paid for my booking.\n\n👤 ${form.name}\n📞 +91${form.phone}\n🧰 ${serviceTitle}\n📅 ${form.date} ${form.time}\n⏱️ ${form.duration}\n🏠 ${form.location || "-"}${map}\n\nRef: ${data.id.slice(-6).toUpperCase()}`;
      const r = { wa: waLink(msg), id: data.id, amount: data.amount, discount: data.discount, track: `/track/${data.pay_token}` };
      if (data.discount) toast.success(t("offer_applied"));
      setResult(r);
      payNow(r);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  const payNow = async (r) => {
    setPaying(true);
    try {
      const { data } = await api.post("/create-order", { booking_id: r.id });
      const ok = await loadRazorpay();
      if (!ok || !window.Razorpay) { toast.error("Could not load Razorpay. Check your connection."); setPaying(false); return; }
      track("payment_start", { booking_id: r.id, amount: data.amount });
      const rzp = new window.Razorpay({
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || data.key_id,
        amount: data.amount, currency: data.currency, order_id: data.order_id,
        name: "CHELA", description: "Booking payment",
        prefill: { name: form.name, contact: `+91${form.phone}`, email: form.email || undefined },
        theme: { color: "#E05624" },
        modal: { ondismiss: () => { setPaying(false); setPayPending(true); toast.error(t("pay_pending")); track("payment_dismissed", { booking_id: r.id }); } },
        handler: async (resp) => {
          try {
            await api.post("/verify-payment", { ...resp, booking_id: r.id });
            track("payment_success", { booking_id: r.id });
            setPaid(true);
            setShowTy(true);
          } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail) || "Payment verification failed");
          } finally { setPaying(false); }
        },
      });
      rzp.on("payment.failed", (x) => { toast.error("Payment failed: " + (x.error?.description || "please try again")); setPaying(false); setPayPending(true); });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start payment");
      setPaying(false);
    }
  };

  if (paid && result) {
    const info = { ...result, when: `${form.date} · ${form.time}` };
    return (
      <>
        <ThankYouModal open={showTy} onClose={() => setShowTy(false)} t={t} info={info} onHome={() => navigate("/")} />
        <div data-testid="booking-success" className="mt-8 chela-card rounded-3xl p-8 text-center" style={{ transform: "none" }}>
          <div className="h-14 w-14 rounded-full flex items-center justify-center mx-auto bg-[#E8F7EE]"><Check size={28} className="text-[#1B8A4B]" /></div>
          <h2 className="mt-5 font-display font-bold text-2xl text-[#121316]">{t("ty_title")}</h2>
          <p className="mt-2 text-[#5A5D65]">{t("ty_sub")}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <a data-testid="booking-continue-wa" href={result.wa} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-2 h-13 px-7 py-3.5 rounded-full btn-whatsapp font-bold"><MessageCircle size={18} /> {t("ty_wa")}</a>
            <button onClick={() => navigate("/")} className="inline-flex items-center justify-center h-13 px-7 py-3.5 rounded-full btn-dark font-bold">{t("back_home")}</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <form data-testid="booking-form" onSubmit={submit} className="mt-6 chela-card rounded-3xl p-5 sm:p-8 space-y-7 pb-4" style={{ transform: "none" }}>
      <div>
        <h1 className="font-display font-extrabold text-3xl text-[#121316]">{t("bk_title")}</h1>
        <p className="mt-2 text-[#5A5D65]">{t("bk_sub")}</p>
        <p data-testid="booking-offer-note" className="mt-3 inline-block text-sm font-bold text-[#E05624] bg-[#FFF1EC] rounded-full px-4 py-1.5">{currency}{price}{t("per_hour")} · {t("offer_line")}</p>
      </div>
      <Step n={1} title={t("s_what")}>
        <ServiceChips services={SVC_ORDER} lang={lang} value={form.service} onChange={put("service")} />
        <ServiceScope svc={SVC_ORDER.find((s) => s.id === form.service)} lang={lang} t={t} />
        <textarea data-testid="booking-desc" value={form.description} onChange={set("description")} placeholder={t("f_desc_ph")} rows={2} className="w-full p-4 rounded-xl border border-[#E5E0D8] bg-white text-[#121316] outline-none focus:border-[#E05624] focus:ring-2 focus:ring-[#E05624]/20 transition resize-none" />
      </Step>
      <Step n={2} title={t("s_when")}>
        <DateChips t={t} value={form.date} onChange={setDate} />
        <div className="flex flex-wrap items-center gap-3"><span className="text-sm font-bold text-[#121316]">{t("f_duration")}</span><HoursStepper t={t} lang={lang} value={hours} onChange={setHours} /></div>
        <TimePicker key={form.date} t={t} lang={lang} date={form.date} hours={hours} value={form.time} onChange={put("time")} />
      </Step>
      <Step n={3} title={t("s_where")}>
        <LocationPicker t={t} coords={coords} onCoords={setCoords} />
        <input data-testid="booking-location" value={form.location} onChange={set("location")} placeholder={t("f_location_ph")} className={inputCls} />
        <div className="grid sm:grid-cols-2 gap-3">
          <input data-testid="booking-name" value={form.name} onChange={set("name")} required placeholder={t("f_name")} className={inputCls} />
          <PhoneInput testid="booking-phone" value={form.phone} onChange={put("phone")} error={phoneErr} />
        </div>
        <button type="button" data-testid="booking-more" onClick={() => setMore((m) => !m)} className="text-sm font-bold text-[#E05624] underline">{more ? t("f_less") : t("f_more")}</button>
        {more && (
          <div className="grid sm:grid-cols-3 gap-3">
            <select data-testid="booking-city" value={form.city} onChange={set("city")} className={inputCls}>
              {cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
            </select>
            <input data-testid="booking-email" type="email" value={form.email} onChange={set("email")} placeholder={t("f_email_ph")} className={inputCls} />
            <input data-testid="booking-ref" value={form.referral_code} onChange={set("referral_code")} placeholder={t("f_ref_ph")} className={inputCls} />
            <button type="button" data-testid="booking-other-city" onClick={onWaitlist} className="text-xs font-semibold text-[#E05624] underline text-left">{t("other_city")}</button>
          </div>
        )}
      </Step>
      <Honeypot value={form.website} onChange={put("website")} />
      <ConsentTick lang={lang} testid="booking-consent" checked={form.consent} onChange={put("consent")} />
      <p data-testid="booking-rules" className="text-xs text-[#3A3D45] bg-[#FCF4E6] border border-[#F0E2C4] rounded-xl px-4 py-3">{t("bk_rules")}</p>
      {payPending && (
        <div data-testid="booking-pay-pending" className="rounded-2xl border-2 border-[#C0392B] bg-[#FDECEC] p-4 text-center">
          <p className="font-bold text-[#C0392B]">{t("pay_pending_title")}</p>
          <button type="button" data-testid="booking-pay-now" onClick={() => payNow(result)} disabled={paying} className="mt-3 w-full h-12 rounded-full btn-saffron font-bold disabled:opacity-60">{t("pay_now")} {currency}{estAmount}</button>
          <QrPay bookingId={result?.id} t={t} onPaid={() => { setPaid(true); setShowTy(true); }} />
        </div>
      )}
      {result?.discount > 0 && <p data-testid="booking-offer-applied" className="text-sm text-center font-bold text-[#1B8A4B] bg-[#E9F7EF] rounded-xl px-4 py-2.5">{t("offer_applied")} -{currency}{result.discount}</p>}
      <div data-testid="booking-paybar" className="sticky bottom-3 z-30 rounded-3xl bg-[#121316] text-white p-3 pl-5 flex items-center gap-3 shadow-[0_18px_40px_rgba(0,0,0,0.3)]">
        <div className="min-w-0 flex-1">
          <div data-testid="booking-summary" className="text-xs text-white/70 truncate">{form.date ? `${form.date} · ` : ""}{form.time || t("slot_pick")} · {hours}h</div>
          <div data-testid="booking-total" className="font-display font-extrabold text-2xl">{currency}{estAmount}</div>
        </div>
        <button data-testid="booking-submit" type="submit" disabled={submitting || paying} className="h-14 px-6 rounded-full btn-saffron font-bold text-base inline-flex items-center gap-2 disabled:opacity-60 shrink-0">
          {submitting || paying ? t("pay_opening") : <>{t("submit_book")} <ArrowRight size={18} /></>}
        </button>
      </div>
      <p data-testid="booking-pay-note" className="text-xs text-center text-[#5A5D65]">{t("pay_note")}</p>
      <LegalLinks t={t} />
    </form>
  );
}

function WaitlistForm({ t, cities }) {
  const [form, setForm] = useState({ name: "", phone: "", city: "", use_case: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [phoneErr, setPhoneErr] = useState("");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const put = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.city.trim()) { toast.error(t("required")); return; }
    if (!isValidPhone(form.phone)) { setPhoneErr(t("phone_err")); return; }
    setPhoneErr("");
    setSubmitting(true);
    try {
      await api.post("/waitlist", { ...form, src: getSrc() });
      track("waitlist_submit", { city: form.city });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div data-testid="waitlist-success" className="mt-8 chela-card rounded-3xl p-8 text-center" style={{ transform: "none" }}>
        <div className="h-14 w-14 rounded-full bg-[#E8F7EE] flex items-center justify-center mx-auto"><Check size={28} className="text-[#1B8A4B]" /></div>
        <h2 className="mt-5 font-display font-bold text-2xl text-[#121316]">{t("wl_success")}</h2>
      </div>
    );
  }
  return (
    <form data-testid="waitlist-form" onSubmit={submit} className="mt-8 chela-card rounded-3xl p-6 sm:p-8 space-y-5" style={{ transform: "none" }}>
      <div>
        <h1 className="font-display font-extrabold text-3xl text-[#121316]">{t("wl_title")}</h1>
        <p className="mt-2 text-[#5A5D65] flex items-center gap-2"><MapPin size={16} className="text-[#E05624]" /> {t("bk_only_bhopal")}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <Field label={t("f_name")}><input data-testid="waitlist-name" value={form.name} onChange={set("name")} required className={inputCls} /></Field>
        <div><span className="block text-sm font-bold text-[#121316] mb-1.5">{t("f_phone")}</span><PhoneInput testid="waitlist-phone" value={form.phone} onChange={put("phone")} error={phoneErr} /></div>
      </div>
      <Field label={t("f_city")}>
        <select data-testid="waitlist-city" value={form.city} onChange={set("city")} required className={inputCls}>
          <option value="">{t("f_select")}</option>
          {cities.filter((c) => c.status !== "live").map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
          <option value="Other">Other</option>
        </select>
      </Field>
      <Field label={t("wl_use")}><textarea data-testid="waitlist-use" value={form.use_case} onChange={set("use_case")} rows={3} className="w-full p-4 rounded-xl border border-[#E5E0D8] bg-white outline-none focus:border-[#E05624] focus:ring-2 focus:ring-[#E05624]/20 resize-none" /></Field>
      <Honeypot value={form.website} onChange={put("website")} />
      <button data-testid="waitlist-submit" type="submit" disabled={submitting} className="w-full h-14 rounded-full btn-dark font-bold text-base inline-flex items-center justify-center gap-2 disabled:opacity-60">
        {submitting ? "..." : <>{t("wl_submit")} <ArrowRight size={18} /></>}
      </button>
      <LegalLinks t={t} />
    </form>
  );
}
